import { GenericObjectType, RJSFSchema, UiSchema } from '@rjsf/utils';
import { get, patch } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Navigate, useParams } from 'react-router-dom';
import useProfile from '../hooks/useProfile';
import isDocumentTemplate from '../services/util/isDocumentTemplate';
import resolveDocumentId from '../services/util/resolveDocumentId';
import Loading from '../ui/Loading';

export interface DocumentData {
  file: Blob;
  id: string;
  title: string;
}

type Download = [
  (id: string) => void,
  {
    data: DocumentData | null;
    error: boolean;
    loading: boolean;
  },
];

interface FormSchema {
  attachments: {
    name: string;
    id: string;
  }[];
  canSkip: boolean;
  formData: GenericObjectType;
  id: string;
  schema: RJSFSchema;
  template:
    | 'BANK_DETAIL_COLLECTION_V1'
    | 'EXPRESSION_OF_WISHES_V1'
    | 'ISA_APPLICATION_V1'
    | 'ISA_TRANSFER_V1'
    | 'JISA_APPLICATION_V1'
    | 'JISA_TRANSFER_V1'
    | 'MULTREES_DDM_V1'
    | 'NOMINEE_TRANSFER_V1'
    | 'SIPP_CONTRIBUTION_V1'
    | 'SIPP_DDM_V1'
    | 'SIPP_TRANSFER_V1';
  type: 'FORM';
  ui: UiSchema<GenericObjectType>;
  updatedAt?: string;
}

interface DocumentSignSchema extends Omit<FormSchema, 'template'> {
  formData: {
    document: {
      name: string;
      id?: string;
      sharepointFileId?: string;
    };
  };
  template: 'DOCUMENT_SIGN';
}

interface DocumentViewSchema extends Omit<FormSchema, 'template'> {
  formData: {
    document: {
      name: string;
      id?: string;
      sharepointFileId?: string;
    };
  };
  template: 'DOCUMENT_VIEW';
}

// Tick only, so view-shaped, but kept apart so the two document blocks stay untouched.
interface PositiveConsentSchema extends Omit<FormSchema, 'template'> {
  formData: {
    document: {
      name: string;
      id?: string;
      sharepointFileId?: string;
    };
  };
  // Resolved by the API on read, so it is absent on a pack served before the copy existed.
  intro?: string;
  introSigned?: boolean;
  savedAt?: string;
  template:
    | 'POSITIVE_CONSENT_ISA'
    | 'POSITIVE_CONSENT_ISA_APPLICATION'
    | 'POSITIVE_CONSENT_SIPP'
    | 'POSITIVE_CONSENT_SIPP_ISA';
}

interface PanelSchema {
  content: string;
  id: string;
  title: string;
  type: 'PANEL';
}

export interface Step {
  active: boolean;
  content: (
    | DocumentSignSchema
    | DocumentViewSchema
    | FormSchema
    | PanelSchema
    | PositiveConsentSchema
  )[];
  id: string;
  title: string;
}

type DocumentBlock =
  | DocumentSignSchema
  | DocumentViewSchema
  | PositiveConsentSchema;

const isDocumentBlock = (
  item: Step['content'][number],
): item is DocumentBlock =>
  item.type === 'FORM' && isDocumentTemplate(item.template);

interface Data {
  completionTitle?: string;
  id: string;
  linked: {
    id: string;
    completed: boolean;
  } | null;
  message: string;
  signature?: string;
  steps: Step[];
  title: string;
}

interface FormInput {
  formData?: GenericObjectType;
  id: string;
  type: 'FORM';
}

interface PanelInput {
  id: string;
  type: 'PANEL';
}

interface SignatureInput {
  id: string;
  signature: string;
  type: 'SIGNATURE';
}

type Input = FormInput | PanelInput | SignatureInput;

export type FileType = 'DOCUMENT' | 'ATTACHMENT';

export interface FormContextValues {
  data: Data;
  error?: string;
  loading: boolean;
  download: (fileType: FileType) => Download;
  save: (input: Input) => void;
}

export const FormContext = createContext<FormContextValues | null>(null);

interface FormProviderProps {
  children: ReactNode;
}

function FormProvider({ children }: FormProviderProps) {
  const [data, setData] = useState<Data | null>(null);

  const [document, setDocument] = useState<DocumentData | null>(null);

  const [documentError, setDocumentError] = useState(false);

  const [documentLoading, setDocumentLoading] = useState(false);

  const [failed, setFailed] = useState(false);

  const [formError, setFormError] = useState<string>();

  const [formLoading, setFormLoading] = useState(false);

  const { id } = useParams();

  const { contact } = useProfile();

  const getUserInfo = useCallback(async () => {
    const session = await fetchAuthSession();

    if (!session.tokens?.idToken) {
      throw new Error('Not authenticated.');
    }

    return {
      contactId: contact.ContactID,
      token: session.tokens?.idToken.toString(),
    };
  }, [contact.ContactID]);

  const getAttachment = useCallback(
    async (documentId: string) => {
      setDocument(null);
      if (data) {
        // Find the document
        const documentSignForm = data.steps
          .flatMap(({ content }) => content)
          .find(
            (item): item is DocumentBlock =>
              isDocumentBlock(item) &&
              item.attachments?.some((att) => att.id === documentId),
          );

        const formAttachment = documentSignForm?.attachments?.find(
          (att) => att.id === documentId,
        );

        if (formAttachment) {
          setDocumentLoading(true);

          const { token } = await getUserInfo();

          const { response } = get({
            apiName: 'forms',
            options: {
              headers: {
                Accept: 'application/pdf',
                Authorization: token,
              },
            },
            path: `/document/${formAttachment.id}`,
          });

          const { body } = await response;

          const file = await body.blob();

          setDocument({
            file,
            id: formAttachment.id,
            title: formAttachment.name,
          });
        }
      }
    },
    [data, getUserInfo],
  );

  const getDocument = useCallback(
    async (documentId: string) => {
      setDocument(null);
      if (data) {
        // Find the document
        const documentSignForm = data.steps
          .flatMap(({ content }) => content)
          .find(
            (item): item is DocumentBlock =>
              isDocumentBlock(item) &&
              resolveDocumentId(item.formData.document) === documentId,
          );

        if (documentSignForm) {
          setDocumentLoading(true);

          const { contactId, token } = await getUserInfo();

          const { response } = get({
            apiName: 'forms',
            options: {
              headers: {
                Accept: 'application/pdf',
                Authorization: token,
              },
            },
            path: `/user/${contactId}/form/${id}/document/${documentSignForm.id}`,
          });

          const { body } = await response;

          const file = await body.blob();

          setDocument({
            file,
            id: resolveDocumentId(documentSignForm.formData.document),
            title: documentSignForm.formData.document.name,
          });
        }
      }
    },
    [data, getUserInfo, id],
  );

  const getForms = useCallback(async () => {
    setFormLoading(true);

    const { contactId, token } = await getUserInfo();

    try {
      const { response } = get({
        apiName: 'forms',
        options: {
          headers: {
            Authorization: token,
          },
        },
        path: `/user/${contactId}/form/${id}`,
      });

      const { body } = await response;

      const result = (await body.json()) as unknown as Data;

      setData(result);
    } catch (e) {
      setFailed(true);
    }
  }, [getUserInfo, id]);

  const download = useCallback(
    (fileType: FileType): Download => {
      const caller = (documentId: string) => {
        (fileType === 'ATTACHMENT'
          ? getAttachment(documentId)
          : getDocument(documentId)
        )
          .catch(() => {
            setDocumentError(true);
          })
          .finally(() => {
            setDocumentLoading(false);
          });
      };

      return [
        caller,
        {
          data: document,
          error: documentError,
          loading: documentLoading,
        },
      ];
    },
    [document, documentError, documentLoading, getAttachment, getDocument],
  );

  const submitForm = useCallback(
    async (input: Input) => {
      setFormLoading(true);

      const { contactId, token } = await getUserInfo();

      const { response } = patch({
        apiName: 'forms',
        options: {
          body: input as GenericObjectType,
          headers: {
            Authorization: token,
          },
        },
        path: `/user/${contactId}/form/${id}`,
      });

      const { body } = await response;

      const result = (await body.json()) as unknown as Data;

      setData(result);
    },
    [getUserInfo, id],
  );

  const save = useCallback(
    (input: Input) => {
      submitForm(input)
        .catch((e: Data) => {
          setFormError(e.message);
        })
        .finally(() => {
          setFormLoading(false);
        });
    },
    [submitForm],
  );

  useEffect(() => {
    getForms()
      .catch((e: Data) => {
        setFormError(e.message);
      })
      .finally(() => {
        setFormLoading(false);
      });
  }, [getForms]);

  const value = useMemo(
    () => ({
      data: data as Data, // We guard against this being undefined further below,
      download,
      error: formError,
      loading: formLoading,
      save,
    }),
    [data, download, formError, formLoading, save],
  );

  // The consent email links straight to /form/{id}, and that link outlives the pack.
  // A stale one lands on the client's own list rather than a 404 they cannot act on.
  if (failed) {
    return <Navigate to="/actions" replace />;
  }

  if (!data) {
    return <Loading />;
  }

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}

export default FormProvider;
